import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarDays, X } from 'lucide-react';
import { presetRange, reportTimeZone } from '@/lib/dateRange';
import { useBranchStore, useFeatureFlagsStore } from '@/stores';

const presets = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'last7', label: 'Last 7d' },
  { key: 'month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'last30', label: 'Last 30d' },
  { key: 'last90', label: 'Last 90d' },
  { key: 'year', label: 'This Year' },
  { key: 'last_year', label: 'Last Year' },
  { key: 'custom', label: 'Custom Range' },
];

export function DateQuickFilter({ onFilterChange, className = '', defaultActive = 'all', timeZone, branchIds, hideAll = false }) {
  const [active, setActive] = useState(defaultActive);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // iter651/R3 — presets come from the shared lib (local or branch timezone).
  const branches = useBranchStore((st) => st.branches);
  const companyTz = useFeatureFlagsStore((st) => st.companyTz);
  // C3 priority: shared branch tz → company tz → device tz.
  const tz = timeZone || reportTimeZone(branches || [], branchIds) || companyTz;
  const getRange = (key) => presetRange(key, tz);

  const handleSelect = (key) => {
    setActive(key);
    if (key === 'all') {
      onFilterChange(null);
    } else if (key === 'custom') {
      if (customStart && customEnd) {
        onFilterChange({ start: customStart, end: customEnd });
      }
    } else {
      onFilterChange(getRange(key));
    }
  };

  const handleCustomChange = (field, val) => {
    const s = field === 'start' ? val : customStart;
    const e = field === 'end' ? val : customEnd;
    if (field === 'start') setCustomStart(val);
    else setCustomEnd(val);
    if (s && e) onFilterChange({ start: s, end: e });
  };

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`} data-testid="date-quick-filter">
      <CalendarDays size={16} className="text-muted-foreground shrink-0" />
      {!hideAll && <Button
        size="sm"
        variant={active === 'all' ? 'default' : 'outline'}
        className={`h-8 rounded-full text-xs px-3 ${active === 'all' ? 'bg-foreground text-white hover:bg-muted' : ''}`}
        onClick={() => handleSelect('all')}
        data-testid="date-filter-all"
      >
        All
      </Button>}
      {presets.map(p => (
        <Button
          key={p.key}
          size="sm"
          variant={active === p.key ? 'default' : 'outline'}
          className={`h-8 rounded-full text-xs px-3 transition-all ${
            active === p.key ? 'bg-foreground text-white hover:bg-muted shadow-sm' : 'hover:bg-muted'
          }`}
          onClick={() => handleSelect(p.key)}
          data-testid={`date-filter-${p.key}`}
        >
          {p.label}
        </Button>
      ))}
      {active === 'custom' && (
        <div className="flex items-center gap-2 ml-1">
          <Input
            type="date"
            className="h-8 text-xs w-[130px] rounded-full"
            value={customStart}
            onChange={(e) => handleCustomChange('start', e.target.value)}
            data-testid="date-filter-custom-start"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            className="h-8 text-xs w-[130px] rounded-full"
            value={customEnd}
            onChange={(e) => handleCustomChange('end', e.target.value)}
            data-testid="date-filter-custom-end"
          />
        </div>
      )}
      {active !== 'all' && !hideAll && (
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={() => handleSelect('all')} data-testid="date-filter-clear">
          <X size={14} />
        </Button>
      )}
    </div>
  );
}
